import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import PrimaryNavigation from '../../components/ui/PrimaryNavigation';
import ContextualBreadcrumb from '../../components/ui/ContextualBreadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { projectService } from '../../services/projectService';
import { phaseService } from '../../services/phaseService';

const ProjectOverview = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [project, setProject] = useState(null);
  const [phases, setPhases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const projectId = location?.state?.projectId;

  useEffect(() => {
    if (!authLoading && user && projectId) {
      loadProjectDetails();
    } else if (!authLoading && !user) {
      setError('Please log in to view project details');
      setLoading(false);
    } else if (!projectId) {
      setError('No project selected');
      setLoading(false);
    }
  }, [authLoading, user, projectId]);

  const loadProjectDetails = async () => {
    try {
      setLoading(true);
      setError('');

      const [projectData, phasesData] = await Promise.all([
        projectService?.getProjectById(projectId),
        phaseService?.getProjectPhases(projectId),
      ]);

      setProject(projectData);
      setPhases(
        phasesData?.sort((a, b) => a?.phase_order - b?.phase_order) || []
      );
    } catch (err) {
      console.error('Error loading project details:', err);
      setError(err?.message || 'Failed to load project details');
    } finally {
      setLoading(false);
    }
  };

  const dateRange = useMemo(() => {
    if (!phases?.length) return [];

    const validStarts = phases
      ?.map((p) => new Date(p.planned_start_date))
      ?.filter((d) => !isNaN(d));

    const validEnds = phases
      ?.map((p) => new Date(p.planned_end_date))
      ?.filter((d) => !isNaN(d));

    if (!validStarts?.length || !validEnds?.length) return [];

    const minDate = new Date(Math.min(...validStarts));
    const maxDate = new Date(Math.max(...validEnds));

    const dates = [];
    let current = new Date(minDate);

    while (current <= maxDate) {
      dates?.push(new Date(current));
      current?.setDate(current?.getDate() + 1);
    }

    return dates;
  }, [phases]);

  const monthGroups = useMemo(() => {
    const groups = [];
    if (!dateRange?.length) return groups;

    let currentMonth = dateRange?.[0]?.getMonth();
    let currentYear = dateRange?.[0]?.getFullYear();
    let span = 0;

    dateRange?.forEach((date, index) => {
      if (
        date?.getMonth() === currentMonth &&
        date?.getFullYear() === currentYear
      ) {
        span++;
      } else {
        groups?.push({ month: currentMonth, year: currentYear, span });
        currentMonth = date?.getMonth();
        currentYear = date?.getFullYear();
        span = 1;
      }

      if (index === dateRange?.length - 1) {
        groups?.push({ month: currentMonth, year: currentYear, span });
      }
    });

    return groups;
  }, [dateRange]);

  const monthLabel = (monthIndex) => {
    const months = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
    ];
    return months?.[monthIndex];
  };

  const formatDay = (date) => String(date?.getDate())?.padStart(2, '0');

  const phaseColors = {
    Hacking: 'bg-red-500',
    '3 Musketeers': 'bg-purple-500',
    'False Ceiling': 'bg-blue-500',
    Tiling: 'bg-orange-500',
    'Carpentry Measurement': 'bg-amber-600',
    'Carpentry Installation': 'bg-amber-600',
    Painting: 'bg-green-500',
    Clearing: 'bg-teal-500',
    Cleaning: 'bg-teal-500',
    'Paint Touch-Up': 'bg-green-600',
    '3 Musketeers Installation': 'bg-purple-600',
    Handover: 'bg-gray-700',
  };

  const isDateInRange = (date, start, end) => {
    const d = new Date(date);
    const s = new Date(start);
    const e = new Date(end);
    return d >= s && d <= e;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <PrimaryNavigation />
        <ContextualBreadcrumb />
        <div className="main-content-mobile-nav flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <PrimaryNavigation />
        <ContextualBreadcrumb />
        <div className="main-content-mobile-nav">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <PrimaryNavigation />
      <ContextualBreadcrumb />

      <div className="main-content-mobile-nav">
        <div className="bg-card border border-border rounded-lg p-6 mb-6">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="min-w-0 flex-1">
              <h1 className="text-2xl font-bold text-foreground mb-2 break-words">
                {project?.name}
              </h1>

              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Icon name="Calendar" size={16} />
                <span>
                  {project?.start_date} - {project?.end_date}
                </span>
              </div>
            </div>

            <Button
              variant="outline"
              iconName="ArrowLeft"
              iconPosition="left"
              onClick={() => navigate('/boss-home-page')}
            >
              Back
            </Button>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <h2 className="text-xl font-semibold text-foreground mb-6">
            Project Timeline
          </h2>

          <div className="overflow-x-auto">
            <div
              className="inline-grid border border-border min-w-max"
              style={{
                gridTemplateColumns: `180px repeat(${dateRange?.length}, 24px)`,
              }}
            >
              <div className="border-r border-border bg-muted"></div>
              {monthGroups?.map((group, index) => (
                <div
                  key={index}
                  className="text-xs font-semibold text-center border-l border-border bg-muted py-2"
                  style={{ gridColumn: `span ${group?.span}` }}
                >
                  {monthLabel(group?.month)}
                </div>
              ))}

              <div className="border-r border-border bg-muted"></div>
              {dateRange?.map((date, index) => (
                <div
                  key={index}
                  className="text-[10px] text-center border-l border-border bg-muted py-2"
                >
                  {formatDay(date)}
                </div>
              ))}

              {phases?.map((phase) => {
                const baseColor = phaseColors?.[phase?.phase_name] || 'bg-gray-400';

                const renderRow = (label, rowKey) => (
                  <React.Fragment key={rowKey}>
                    <div className="border-t border-border px-3 py-2 text-xs font-medium leading-tight break-words flex items-center min-h-[40px]">
                      {label}
                    </div>

                    {dateRange?.map((date, idx) => {
                      const active = isDateInRange(
                        date,
                        phase?.planned_start_date,
                        phase?.planned_end_date
                      );

                      return (
                        <div
                          key={idx}
                          className={`border-l border-t border-border min-h-[40px] ${
                            active ? baseColor : 'bg-background'
                          }`}
                        />
                      );
                    })}
                  </React.Fragment>
                );

                if (phase?.phase_name === '3 Musketeers') {
                  return (
                    <React.Fragment key={phase?.id}>
                      {renderRow('3M - Plumber', `${phase?.id}-plumber`)}
                      {renderRow('3M - Electrician', `${phase?.id}-electrician`)}
                      {renderRow('3M - Aircon', `${phase?.id}-aircon`)}
                    </React.Fragment>
                  );
                }

                return renderRow(phase?.phase_name, phase?.id);
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectOverview;
